import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { 
  CuestionarioRequest, 
  CuestionarioResponse,
  EliminarCuestionarioRequest,
  EliminarCuestionarioResponse,
  OpcionesVariablesRequest,
  OpcionesVariablesResponse,
  EliminarCuestipoRequest,
  EliminarCuestipoResponse
} from '../interfaces/cuestionario.interface';
import { IPregunta, IPreguntaRequest } from '../interfaces/pregunta.interface';

export interface GetCuestiposRequest {
  sUsuario: string;
  iAccion: number;
  sCuest: string;
  sTipo: string;
}

export interface GetCuestiposResponse {
  success: boolean;
  data: {
    sTipo: string;
  }[];
}

export interface CuestiposRequest {
  sUsuario: string;
  iAccion: number;
  sCuest: string;
  sTipo: string;
}

export interface GetTablasResponse {
  success: boolean;
  data: {
    sTabla: string;
    sEtiqueta: string;
  }[];
}

export interface GetTiposResponse {
  success: boolean;
  data: {
    sCampo: string;
    sEtiqueta: string;
  }[];
}

export interface GetOpcionesVariablesResponse {
  success: boolean;
  data: {
    sOpcion: string;
    sValor: string;
  }[];
}

export interface GetCustomizablesResponse {
  success: boolean;
  data: {
    descripcion: string;
    caracteristica: string;
    itipo: number;
  }[];
}

export interface GuardarPreguntaRequest {
  que: number;
  sClaveCues: string;
  iNumPreg: number;
  sQuePregunta: string;
  sTabla: string;
  sTipo: string;
  sCampo: string;
  iTipoResp: number;
  bActualizar: number;
  bMostrar: number;
  bGrabar: number;
}

export interface GuardarPreguntaResponse {
  success: boolean;
  data: {
    error: number;
  };
}

export interface EliminarPreguntaRequest {
  que: number;
  sClaveCues: string;
  iNumPreg: number;
}

export interface EliminarPreguntaResponse {
  success: boolean;
  data: {
    error: number;
  };
}

export interface ActualizarPreguntaRequest {
  que: number;
  sClaveCues: string;
  iNumPreg: number;
  sQuePregunta: string;
  sTabla: string;
  sTipo: string;
  sCampo: string;
  iTipoResp: number;
  bActualizar: number;
  bMostrar: number;
  bGrabar: number;
}

export interface ActualizarPreguntaResponse {
  success: boolean;
  data: {
    error: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CuestionariosService {
  private apiUrl = `${environment.apiNewBackBaseUrl}`;

  constructor(private http: HttpClient) {}

  getCuestionarios(request: CuestionarioRequest): Observable<CuestionarioResponse> {
    return this.http.post<CuestionarioResponse>(`${this.apiUrl}Cuestionarios/cmspa_cuestionarioList`, request);
  }

  eliminarCuestionario(request: EliminarCuestionarioRequest): Observable<EliminarCuestionarioResponse> {
    return this.http.post<EliminarCuestionarioResponse>(`${this.apiUrl}Cuestionario/GetCustnarioErr`, request);
  }

  actualizarCuestionario(request: EliminarCuestionarioRequest): Observable<EliminarCuestionarioResponse> {
    request.que = 2; // Para actualizar
    return this.http.post<EliminarCuestionarioResponse>(`${this.apiUrl}Cuestionario/GetCustnarioErr`, request);
  }

  getOpcionesVariables(request: OpcionesVariablesRequest): Observable<OpcionesVariablesResponse> {
    return this.http.post<OpcionesVariablesResponse>(`${this.apiUrl}OpcionesSolicitud/GetOpcionesVariables`, request);
  }

  getCuestionarioEditar(sClave: string): Observable<CuestionarioResponse> {
    const request = {
      sClave,
      sDescripcion: "",
      iTipo: 0,
      iGestion: 0,
      bTodos: 0
    };
    return this.http.post<CuestionarioResponse>(`${this.apiUrl}Cuestionario/GetCustnario5`, request);
  }

  getCuestipos(request: GetCuestiposRequest): Observable<GetCuestiposResponse> {
    return this.http.post<GetCuestiposResponse>(`${this.apiUrl}Cuestionario/GetCuestipos4`, request);
  }

  eliminarCuestipo(request: EliminarCuestipoRequest): Observable<EliminarCuestipoResponse> {
    return this.http.post<EliminarCuestipoResponse>(`${this.apiUrl}Cuestionario/GetCuestiposErr`, request);
  }

  guardarTipoCuestionario(request: CuestiposRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}Cuestionario/GetCuestiposErr`, request);
  }

  getPreguntas(sClaveCues: string): Observable<any> {
    const request: IPreguntaRequest = {
      tope: '999',
      sClaveCues: sClaveCues,
      iNumPreg: 0,
      sQuePregunta: '',
      sTabla: '',
      sTipo: '',
      sCampo: '',
      iTipoResp: 0,
      bActualizar: 0,
      bMostrar: 0,
      bGrabar: 0
    };

    return this.http.post<any>(`${this.apiUrl}Cuestionario/GetPreguntas4`, request);
  }

  getTablas(): Observable<GetTablasResponse> {
    const request = {
      iIdioma: 0
    };
    return this.http.post<GetTablasResponse>(`${this.apiUrl}Cuestionario/GetTbleIdims`, request);
  }

  getTipos(sTabla: string): Observable<GetTiposResponse> {
    const request = {
      sTabla: sTabla,
      iIdioma: 0
    };
    return this.http.post<GetTiposResponse>(`${this.apiUrl}Cuestionario/GetCamIdims`, request);
  }

  getTiposPorTabla(sTabla: string, sCampo: string): Observable<GetOpcionesVariablesResponse> {
    const request = {
      sTabla: sTabla,
      sCampo: sCampo,
      iIdioma: 0
    };
    return this.http.post<GetOpcionesVariablesResponse>(`${this.apiUrl}OpcionesSolicitud/GetOpcionesVariables`, request);
  }

  getCustomizables(): Observable<GetCustomizablesResponse> {
    const request = {
      iAccion: 0,
      sCampo: "",
      sCaract: "",
      iNumReg: 9999
    };
    return this.http.post<GetCustomizablesResponse>(`${this.apiUrl}Customizable/NvoscampList`, request);
  }

  guardarPregunta(request: GuardarPreguntaRequest): Observable<GuardarPreguntaResponse> {
    return this.http.post<GuardarPreguntaResponse>(`${this.apiUrl}Cuestionario/GetPreguntasErr`, request);
  }

  eliminarPregunta(request: EliminarPreguntaRequest): Observable<EliminarPreguntaResponse> {
    return this.http.post<EliminarPreguntaResponse>(`${this.apiUrl}Cuestionario/GetPreguntasErr`, request);
  }

  actualizarPregunta(request: ActualizarPreguntaRequest): Observable<ActualizarPreguntaResponse> {
    return this.http.post<ActualizarPreguntaResponse>(`${this.apiUrl}Cuestionario/GetPreguntasErr`, request);
  }

  getGuiones(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}Cuestionario/GetGuion4`, request);
  }

  guardarGuion(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}Cuestionario/GetGuionErr`, request);
  }

  guardarOpcion(request: any): Observable<any> {
    return this.http.post(`${this.apiUrl}Cuestionario/GetOpcRtaErr`, request);
  }

  getOpciones(request: any): Observable<any> {
    return this.http.post(`${this.apiUrl}Cuestionario/GetOpcRta`, request);
  }

  eliminarOpcion(request: any): Observable<any> {
    return this.http.post(`${this.apiUrl}Cuestionario/GetOpcRtaErr`, request);
  }

  getScripts(request: any): Observable<any> {
    return this.http.post(`${this.apiUrl}Cuestionario/GetScrpt4`, request);
  }

  guardarScript(request: any): Observable<any> {
    return this.http.post(`${this.apiUrl}Cuestionario/GetScrpterr`, request);
  }
} 